'use client';
import React from 'react';

import { Breadcrumbs } from '../../../../components/breadcrumbs';
import { PageComponentImage } from '../../../../components/page-components/image/page-component-image';
import { PageComponentMain } from '../../../../components/page-components/main/page-component-main';
import useGetMobileAppWebViewHeader from '../../../../hooks/use-get-mobile-app-web-view';
import { IMeetingPlacePageEventItem } from '@/lib/fragments/meetingPlacePageEventItem';
import { IMeetingplacePageHomeMinified } from '@/lib/fragments/meetingPlacePageHome-minified';
import { IGtmParameters, useSendGtmEvent } from '@/hooks/use-send-gtm-event';
import useNewsletterPopupAutomation from '@/hooks/useNewsletterPopupAutomation';
import { GtmPageView } from '@/components/page-components/analytics/gtm-page-view';
import { IContentfulImage } from '@/interfaces/contentful-image.interface';
import { content } from '../../../../../tailwind.config';
import { useUser } from '@/providers/UserProvider';

interface MeetingPlacePageEventProps {
  metaData: any;
  node_locale: string;
  preview: boolean;
  eventData: IMeetingPlacePageEventItem | null;
  homeData: IMeetingplacePageHomeMinified | null;
  parent?: any;
  isLivliFamily: boolean;
}

export const MeetingPlacePageEvent = ({
  metaData,
  node_locale,
  preview,
  eventData,
  homeData,
  parent,
  isLivliFamily,
}: MeetingPlacePageEventProps) => {
  const { user } = useUser();
  const page = eventData;
  const homepage = homeData;
  const imageURL = page?.mediaField?.jsonBynderAsset?.originalUrl
    ? page?.mediaField?.jsonBynderAsset?.originalUrl
    : undefined;
  const pageName = page?.name ? page.name : '';

  const isWebView = useGetMobileAppWebViewHeader();

  let logo: IContentfulImage | undefined = undefined;
  if (page?.tenant) {
    logo = page?.tenant?.logo
      ? page.tenant.logo
      : page?.tenant?.brand
        ? page.tenant.brand?.logo
        : undefined;
  }

  const gtmParams: IGtmParameters = {
    event: 'view_event_detail',
    detail_name: pageName,
    contentful_id: page?.sys?.id || '',
    offer_type: 'Offer Type Not Available',
    user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
  };
  useSendGtmEvent(gtmParams);

  useNewsletterPopupAutomation();

  return (
    <>
      {page && (
        <>
          <GtmPageView
            homepage={homepage}
            language={node_locale}
            pageTitle={page?.name ?? ''}
          />
          <main className="bg-background-primary-light px-6 lg:-ml-22 lg:bg-transparent lg:px-0">
            <Breadcrumbs
              breadcrumbs={[pageName]}
              homeText={homepage?.breadcrumbHomeText ?? ''}
              cssClass={`lg:ml-22 ${isWebView ? '!pt-8' : ''}`}
              node_locale={node_locale}
              parent={parent}
            />
            <div className="grid grid-cols-11 gap-6 pt-5">
              <div className="col-span-12 bg-background-primary-light lg:col-span-5 lg:bg-transparent">
                <PageComponentImage
                  imageURL={imageURL}
                  imageAlt={page?.mediaField?.altText}
                  // tags={page?.eventTypesCollection?.items}
                  stickyImage={true}
                  // header={pageName}
                  // eventDateTo={page?.endDate}
                  // eventDateFrom={page?.startDate}
                  // hasShadedOverlay={true}
                  node_locale={node_locale}
                />
              </div>
              <div className="col-span-12 lg:col-span-6">
                <PageComponentMain
                  logo={logo}
                  pageTitle={page?.name ?? ''}
                  tenantName={page?.tenant?.name ?? page?.tenant?.brand?.name}
                  header={pageName}
                  caption={page?.caption}
                  tags={page?.eventTypesCollection?.items?.map(
                    (item) => item.name,
                  )}
                  richText={page?.body}
                  node_locale={node_locale}
                  showSocialShare={
                    homepage?.meetingPlace?.showSocialShare ?? false
                  }
                  validTo={page?.endDate}
                  validFrom={page?.startDate}
                  favouriteId={page?.sys?.id}
                  favouriteType="event"
                  contentful_id={page?.sys?.id}
                  meetingPlaceHomePage={homepage?.meetingPlace}
                  isLivliFamily={isLivliFamily}
                />
              </div>
            </div>
          </main>
        </>
      )}
    </>
  );
};

export default MeetingPlacePageEvent;
